import axios, { AxiosHeaders, AxiosRequestConfig } from 'axios';
import { environment } from 'routes/environment.router';
import { authStorage } from 'functions/auth-session';

const api = axios.create({
    baseURL: environment.api_url,
});

const baseHeaders = () => ({
    'X-Api-Key': environment.api_key,
    // O servidor pode rodar em UTC: sem isto, horas sem fuso saem erradas para quem lê.
    'X-Time-Zone': Intl.DateTimeFormat().resolvedOptions().timeZone,
});

api.interceptors.request.use((config) => {
    config.headers = config.headers || new AxiosHeaders();

    Object.entries(baseHeaders()).forEach(([name, value]) => config.headers.set(name, value));

    const session = authStorage.read();

    if (session) config.headers.set('Authorization', `Bearer ${session.token}`);

    return config;
});

const signOutAndReload = () => {
    authStorage.clear();
    window.location.reload();
};

/// Uma renovação por vez: várias telas com 401 esperam a mesma troca.
let refreshing: Promise<string> | null = null;

export const refreshSession = (): Promise<string> => {
    const session = authStorage.read();

    if (!session?.refreshToken) return Promise.reject(new Error('Sem refresh token'));

    refreshing ??= axios
        .post(
            `${environment.api_url.replace(/\/$/, '')}/refresh-token`,
            { refreshToken: session.refreshToken },
            { headers: baseHeaders() })
        .then((reply) => {
            const payload = reply.data.data;

            authStorage.write({
                ...session,
                token: payload.token,
                refreshToken: payload.refreshToken,
                expiresAt: Date.now() + payload.expiresIn * 1000,
            });

            return payload.token as string;
        })
        .finally(() => {
            refreshing = null;
        });

    return refreshing;
};

api.interceptors.response.use(
    (reply) => reply,
    async (error) => {
        const original: (AxiosRequestConfig & { _retry?: boolean }) | undefined = error?.config;

        // Token vencido: renova e repete uma vez; se falhar, derruba a sessão e volta ao login.
        if (error?.response?.status !== 401 || !original || original._retry || !authStorage.read()) {
            return Promise.reject(error);
        }

        original._retry = true;

        try {
            const token = await refreshSession();

            original.headers = { ...original.headers, Authorization: `Bearer ${token}` };

            return api(original);
        } catch {
            signOutAndReload();
            return Promise.reject(error);
        }
    },
);

export default api;
